/* Copyright (c) 2020-present CloudIO Inc */

import { getManager } from '../../../componentregistry/registry/Manager';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionParallel';

const ActionParallelManager: ActionManagerFn<'ActionParallel'> = (
  platform: Platform,
): ActionManager<'ActionParallel'> => {
  return {
    ...baseActionManager(platform, 'ActionParallel'),
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action',
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'Parallel Actions',
    icon: { iconName: 'grip-lines' },
    description: `**Overview**<br/>
The Parallel Actions component is a flow control container designed to execute multiple actions simultaneously rather than in the standard sequential order. In a typical action chain, "Action B" waits for "Action A" to complete before starting. This component breaks that dependency, triggering all its child actions at the exact same moment.<br/><br/>
This tool is essential for performance optimization when dealing with independent tasks. By running operations concurrently, the total time required to complete the sequence is determined by the longest single action, rather than the sum of all actions.

**Key Features**<br/>
* **Concurrent Execution:** Starts all child actions immediately, without waiting for the previous one to finish.
* **Generic Container:** Configured to accept any standard Action component, allowing for flexible combinations of logic (e.g., mixing API calls with UI updates).
* **Performance Boosting:** Significantly reduces wait times for users when multiple non-dependent server requests or calculations need to happen (e.g., loading three different charts).

**Use Cases**<br/>
* **Dashboard Loading:** Fetching data for "Sales", "Inventory", and "HR" widgets simultaneously when a page loads, so the user sees results faster.
* **Independent Updates:** Sending a notification email while simultaneously logging an audit record to the database—two tasks that don't rely on each other.
* **UI & Data Sync:** Hiding a loading spinner and showing a success message at the same time.

**Important Note**<br/>
Do not use this component if the actions are dependent on each other. For example, if Action B needs the recordId created by Action A, they must run sequentially, not in parallel.`,
  };
};

export default ActionParallelManager;
